import { resolveAccessibleMenuRowHoverRgb } from './wcagContrast';

/**
 * Space-separated RGB. Light presets use very light grays (reads as white on video).
 *
 * - `--rtk-colors-background-800`: normal control bar `button:hover` fill.
 * - `--rtk-colors-background-1000`: leave uses `:host(.leave:hover) button` with **1000** on
 *   `rtk-controlbar-button`, not 800. Must set on `rtk-leave-button` and/or that inner host.
 *
 * `rtk-controlbar` renders children via nested shadow trees (`Render`), so `rtk-leave-button` is
 * not always a direct child of `controlBar.shadowRoot` — shallow `querySelector` misses it.
 */
/** ~#9a9a9a — lighter than charcoal hover; still avoids preset near-white (#ebebeb) flash on video. */
export const SCOPED_CONTROLBAR_HOVER_BG_800 = '154 154 154';
export const SCOPED_LEAVE_HOVER_BG_1000 = SCOPED_CONTROLBAR_HOVER_BG_800;

/**
 * Legacy fixed dark row for more-menu items (before theme + WCAG hover).
 * @deprecated Prefer theme-derived hover via `resolveAccessibleMenuRowHoverRgb`.
 */
export const SCOPED_MORE_MENU_ROW_HOVER_BG_800 = '76 76 76';

/**
 * @param {ShadowRoot | null} scopeRoot
 * @param {string} selector
 * @param {number} [maxDepth]
 * @returns {HTMLElement[]}
 */
export function collectElementsDeepShadow(scopeRoot, selector, maxDepth = 24) {
  /** @type {HTMLElement[]} */
  const out = [];
  const seen = new Set();

  function walk(root, depth) {
    if (!root || depth <= 0) return;
    root.querySelectorAll(selector).forEach((el) => {
      if (!seen.has(el)) {
        seen.add(el);
        out.push(el);
      }
    });
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) walk(el.shadowRoot, depth - 1);
    });
  }

  walk(scopeRoot, maxDepth);
  return out;
}

/**
 * @param {HTMLElement | null} meetingHostEl outer or inner `<rtk-meeting>`
 */
function patchLeaveButtonsInMeetingShadow(meetingHostEl) {
  const sr = meetingHostEl?.shadowRoot;
  if (!sr) return;

  const leaveHosts = collectElementsDeepShadow(sr, 'rtk-leave-button');
  for (const leaveBtn of leaveHosts) {
    leaveBtn.style.setProperty('--rtk-colors-background-1000', SCOPED_LEAVE_HOVER_BG_1000);
    const controlbarBtn = leaveBtn.shadowRoot?.querySelector('rtk-controlbar-button');
    if (controlbarBtn) {
      controlbarBtn.style.setProperty('--rtk-colors-background-1000', SCOPED_LEAVE_HOVER_BG_1000);
    }
  }
}

/**
 * Overflow controls (`rtk-pip-toggle`, `rtk-mute-all-button`, …) use `slot="more-elements"`.
 * Hover uses `--rtk-colors-background-800`: light theme-aware fill so `headerColor` + brand icon
 * (`buttonBackground`) stay readable (see `resolveAccessibleMenuRowHoverRgb`).
 *
 * @param {ShadowRoot | null} meetingShadowRoot
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
function patchMoreElementsSlotRowHover(meetingShadowRoot, themeDetails) {
  if (!meetingShadowRoot) return;

  const hoverRgb = resolveAccessibleMenuRowHoverRgb(themeDetails, {
    fallbackRgb: SCOPED_CONTROLBAR_HOVER_BG_800,
    minRatio: 4.5,
  });

  const slotHosts = collectElementsDeepShadow(meetingShadowRoot, '[slot="more-elements"]');
  for (const host of slotHosts) {
    host.style.setProperty('--rtk-colors-background-800', hoverRgb);
    const controlbarBtn = host.shadowRoot?.querySelector('rtk-controlbar-button');
    if (controlbarBtn) {
      controlbarBtn.style.setProperty('--rtk-colors-background-800', hoverRgb);
    }
  }
}

/**
 * @param {HTMLElement | null} meetingHostEl outer or inner `<rtk-meeting>`
 * @param {object | null | undefined} themeDetails from `getMeetingThemeDetails`
 */
export function applyScopedControlbarHoverBackground(meetingHostEl, themeDetails) {
  if (!meetingHostEl?.shadowRoot) return;

  patchLeaveButtonsInMeetingShadow(meetingHostEl);

  const sr = meetingHostEl.shadowRoot;

  const controlBar = sr.querySelector('rtk-controlbar');
  if (controlBar) {
    controlBar.style.setProperty('--rtk-colors-background-800', SCOPED_CONTROLBAR_HOVER_BG_800);
  }

  patchMoreElementsSlotRowHover(sr, themeDetails);

  const setupMedia = sr
    .querySelector('rtk-setup-screen')
    ?.shadowRoot?.querySelector('#setupcontrols-media');
  if (setupMedia) {
    setupMedia.style.setProperty('--rtk-colors-background-800', SCOPED_CONTROLBAR_HOVER_BG_800);
  }

  const innerMeeting = sr.querySelector('rtk-meeting');
  if (innerMeeting) {
    applyScopedControlbarHoverBackground(innerMeeting, themeDetails);
  }
}
